<template>
	<table class="form-details table-style table--first-column-headings sz-1 w-100">
		<tbody>
			<tr v-for="property in gridData" :key="property.label" :class="{'form-details-dialog__row--list': !!property.references }">
				<th class="form-details-dialog__table-header text-left">
					{{ property.label }}
				</th>
				<td>
					<ul v-if="checkPropertyType(property, 'reference')">
						<li v-for="(item , i) in property.references" :key="i" class="flex gap-3">
							<Inline v-if="item.Type" svg="form" class="text-icon-subtle flex--no-shrink" />
							{{ item.Name }}
						</li>
					</ul>
					<CAvatar
						v-else-if="checkPropertyType(property, 'avatar')"
						:name="property.value"
						:avatar="property.avatar"
						:wrap="true"
						:is-deleted="property.isDeleted"
					/>
					<span v-else class="flex gap-3">
						<Inline
							v-if="property.icon"
							:svg="property.icon"
							class="text-icon-subtle flex--no-shrink"
							:class="{ 'form-details__cogicon': (property.label === 'Payment' && paymentIsCognitoPay) }"
						/>
						{{ property.value }}
					</span>
				</td>
			</tr>
		</tbody>
	</table>
</template>

<script lang="ts">
	import { defineComponent, onMounted, ref, computed } from 'vue';
	import type { PropType } from 'vue';
	import CAvatar from 'src/components/auth/Avatar.vue';
	import { getAvatar } from '@cognitoforms/utils/avatar';
	import clickOutside from 'src/utilities/clickOutside';
	import type { LookupFormReference } from '@cognitoforms/types/server-types/services/forms/lookup-form-reference';
	import session from '@cognitoforms/api/services/session';
	import type { BasicUserMeta } from '@cognitoforms/types/server-types/model/basic-user-meta';

	interface GridData {
		label: string;
		type?: string;
		value: string | number | BasicUserMeta | null;
		references?: LookupFormReference[];
		icon?: string;
		avatar?: string;
		isDeleted?: boolean;
	}

	export default defineComponent({
		name: 'CFormDetailsDialog',
		components: { CAvatar },
		directives: { clickOutside },
		props: {
			id: { type: String, required: true },
			title: { type: String, required: true },
			name: { type: String, required: true },
			url: { type: String, required: true },
			folder: { type: String, default: null },
			theme: { type: String, default: null },
			createdBy: { type: Object as PropType<BasicUserMeta>, default: () => null },
			createdOn: { type: Date, default: () => null },
			lastModifiedBy: { type: Object as PropType<BasicUserMeta>, default: () => null },
			lastModifiedOn: { type: Date, default: () => null },
			entries: { type: Number, default: null },
			storage: { type: String, default: null },
			payment: { type: String, default: null },
			availabilityStart: { type: Date, default: () => null },
			availabilityEnd: { type: Date, default: () => null },
			sharedAsTemplate: { type: Boolean, default: false },
			encryption: { type: Boolean, default: false },
			liveCustomTemplates: { type: Number, default: null },
			references: { type: Array as PropType<LookupFormReference[]>, default: () => [] },
			referencedBy: { type: Array as PropType<LookupFormReference[]>, default: () => [] }
		},
		setup(props) {
			const userIsDeleted = (user: BasicUserMeta) => {
				return user !== null && user.IsDeleted;
			};

			const getUserAvatar = (user: BasicUserMeta) => {
				if (user === null)
					return null;

				return getAvatar(user);
			};

			const getUserName = (user: BasicUserMeta) => {
				if (user === null)
					return null;
				else if (userIsDeleted(user))
					return user.Email + ' (Deleted)';
				else if (user.LastName)
					return user.FirstName + ' ' + user.LastName;
				return user.FirstName;
			};

			const formatTime = (val: Date | string) => {
				if (val == null)
					return null;
				const date = new Date(val);
				const formatter = new Intl.DateTimeFormat([], { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' });
				return formatter.format(date).replace(',', '');
			};

			const formatAvailability = (start: Date | string, end: Date | string) => {
				if (!start && !end)
					return null;

				const now = new Date();
				const startDate = (start) ? new Date(start) : new Date(-1);
				const endDate = (end) ? new Date(end) : new Date(Infinity);

				if (((startDate < now) || !start) && ((endDate > now) || !end)) {
					let str = 'Available';
					const startJoinWord = (end) ? ' from ' : ' since ';
					str += (start ? startJoinWord + formatTime(start) : '');
					str += (end ? ' until ' + formatTime(end) : '');
					return str;
				}
				else if (endDate < now)
					return 'Unavailable since ' + formatTime(end);
				else if (startDate > now)
					return 'Unavailable until ' + formatTime(start);

				return null;
			};

			const sortReferences = (references: LookupFormReference[]): LookupFormReference[] => {
				return references.sort((a, b) => a.Name.localeCompare(b.Name));
			};

			const paymentIsCognitoPay = computed(() => props.payment === 'Cognito Pay');

			const gridData = ref<GridData[]>([]);
			const generateGridData = () => {
				const hideLastModified = (props.createdBy?.Id === props.lastModifiedBy?.Id)
					&& (props.createdOn?.getTime() === props.lastModifiedOn?.getTime());

				const isFriendlyFormNamesEnabled = session.value?.enabledFeatures.FriendlyFormNames;
				const data: GridData[] = [
					...(isFriendlyFormNamesEnabled ? [{
						label: 'Title',
						value: props.title
					}] : []),
					{
						label: 'Name',
						value: props.name
					},
					...(isFriendlyFormNamesEnabled ? [{
						label: 'Url',
						value: props.url
					}] : []),
					{
						label: 'ID',
						value: props.id
					},
					{
						label: 'Folder',
						value: props.folder,
						icon: 'folder'
					},
					{
						label: 'Theme',
						value: props.theme
					},
					{
						label: 'Created By',
						type: 'avatar',
						value: getUserName(props.createdBy),
						avatar: getUserAvatar(props.createdBy),
						isDeleted: userIsDeleted(props.createdBy)
					},
					{
						label: 'Created On',
						value: formatTime(props.createdOn)
					},
					{
						label: 'Last Modified By',
						type: 'avatar',
						value: hideLastModified ? null : getUserName(props.lastModifiedBy),
						avatar: getUserAvatar(props.lastModifiedBy),
						isDeleted: userIsDeleted(props.lastModifiedBy)
					},
					{
						label: 'Last Modified On',
						value: hideLastModified ? null : formatTime(props.lastModifiedOn)
					},
					{
						label: 'Entries',
						value: props.entries
					},
					{
						label: 'Storage',
						value: props.storage
					},
					{
						label: 'Payment',
						value: props.payment,
						icon: paymentIsCognitoPay.value ? 'logos/cogicon' : 'credit-card'
					},
					{
						label: 'Availability',
						value: formatAvailability(
							props.availabilityStart,
							props.availabilityEnd
						),
						icon: 'limited-availability'
					},
					{
						label: 'Shared Template',
						value: props.sharedAsTemplate ? 'Yes' : null,
						icon: 'shared-template'
					},
					{
						label: 'Encryption',
						value: props.encryption ? 'Enabled' : null,
						icon: 'lock'
					},
					{
						label: 'Custom Document Templates',
						value: props.liveCustomTemplates,
						icon: 'generated-documents'
					},
					{
						label: 'References',
						type: 'reference',
						value: null,
						references: sortReferences(props.references),
						icon: 'lookup'
					},
					{
						label: 'Referenced By',
						type: 'reference',
						value: null,
						references: sortReferences(props.referencedBy),
						icon: 'lookup'
					}
				];

				// Remove null properties so they do not show in table
				gridData.value = data.filter(dataObj => (
					dataObj.references ? dataObj.references.length > 0 : dataObj.value
				));
			};

			const checkPropertyType = (property: GridData, type: string): boolean => {
				return property.type === type;
			};

			onMounted(() => generateGridData());

			return {
				gridData,
				checkPropertyType,
				paymentIsCognitoPay
			};
		}
	});
</script>

<style lang="scss">
	.form-details {

		th *,
		td * {
			overflow-wrap: anywhere;
		}

		:not(.form-details-dialog__row--list) > th {
			vertical-align: middle;
		}

		th {
			width: min(30vw, 200px);
			min-width: 120px;
		}

		.i-simple,
		&__cogicon {
			width: $size__icon--small;
			height: $size__icon--small;
			margin-top: 2px;
		}

		li + li {
			margin-top: $space__2;
		}

		.avatar__image {
			width: $size__avatar--small;
			height: $size__avatar--small;
		}
	}
</style>